/**
 * Command Palette Component
 *
 * Provides hence CLI-like commands in the web app.
 * Commands: next, status, assign, explain, why-not, complete, claim
 */

import { useState, useRef, useEffect, useCallback } from "react";
import type { Conclusion } from "../lib/spindle";
import type { Task, Assignment } from "../lib/usePlanReasoning";

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  conclusions: Conclusion[];
  tasks: Task[];
  assignments: Assignment[];
  readyTasks: string[];
  completedTasks: string[];
  blockedTasks: string[];
  onAppend: (forms: string[]) => void;
}

interface CommandResult {
  type: "info" | "success" | "error" | "list";
  content: string | string[];
}

export default function CommandPalette({
  isOpen,
  onClose,
  conclusions,
  tasks,
  assignments,
  readyTasks,
  completedTasks,
  blockedTasks,
  onAppend,
}: CommandPaletteProps) {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<Array<{ cmd: string; result: CommandResult }>>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = useCallback(
    (cmd: string): CommandResult => {
      const parts = cmd.trim().split(/\s+/);
      const command = parts[0]?.toLowerCase();
      const args = parts.slice(1);

      switch (command) {
        case "help":
        case "?":
          return {
            type: "info",
            content: `Available commands:
  next              Show next actionable tasks
  status            Show all conclusions
  assign            Show agent assignments
  tasks             List all tasks with status
  ready             Show ready tasks
  done              Show completed tasks
  blocked           Show blocked tasks
  explain <lit>     Explain why literal is concluded
  why-not <lit>     Explain why literal is NOT concluded
  complete <task>   Mark task as completed
  claim <task>      Claim task for work
  clear             Clear output
  help              Show this help`,
          };

        case "next":
          const nextTasks = tasks.filter((t) => t.status === "ready");
          if (nextTasks.length === 0) {
            return { type: "info", content: "No tasks ready for action" };
          }
          return {
            type: "list",
            content: nextTasks.map(
              (t) => `${t.id}${t.agentType ? ` [${t.agentType}]` : ""}`
            ),
          };

        case "status":
          const positive = conclusions.filter(
            (c) => c.status === "+D" || c.status === "+d"
          );
          const negative = conclusions.filter(
            (c) => c.status === "-D" || c.status === "-d"
          );
          return {
            type: "info",
            content: `Conclusions: ${conclusions.length} total
  +D/+d (proven): ${positive.length}
  -D/-d (not proven): ${negative.length}

Definite (+D):
${positive
  .filter((c) => c.status === "+D")
  .map((c) => `  ${c.literal}`)
  .join("\n") || "  (none)"}

Defeasible (+d):
${positive
  .filter((c) => c.status === "+d")
  .map((c) => `  ${c.literal}`)
  .join("\n") || "  (none)"}`,
          };

        case "assign":
        case "assignments":
          if (assignments.length === 0) {
            return { type: "info", content: "No assignments derived" };
          }
          return {
            type: "list",
            content: assignments.map(
              (a) => `${a.task} → ${a.agent}${a.agentType ? ` [${a.agentType}]` : ""}`
            ),
          };

        case "tasks":
          if (tasks.length === 0) {
            return { type: "info", content: "No tasks found" };
          }
          return {
            type: "list",
            content: tasks.map(
              (t) =>
                `${t.status.padEnd(12)} ${t.id}${t.agent ? ` (${t.agent})` : ""}`
            ),
          };

        case "ready":
          if (readyTasks.length === 0) {
            return { type: "info", content: "No ready tasks" };
          }
          return { type: "list", content: readyTasks };

        case "done":
        case "completed":
          if (completedTasks.length === 0) {
            return { type: "info", content: "No completed tasks" };
          }
          return { type: "list", content: completedTasks };

        case "blocked":
          if (blockedTasks.length === 0) {
            return { type: "info", content: "No blocked tasks" };
          }
          return { type: "list", content: blockedTasks };

        case "explain":
          if (!args[0]) {
            return { type: "error", content: "Usage: explain <literal>" };
          }
          const literal = args[0];
          const found = conclusions.find((c) => c.literal === literal);
          if (!found) {
            return {
              type: "error",
              content: `Literal "${literal}" not found in conclusions`,
            };
          }
          if (found.status === "+D" || found.status === "+d") {
            return {
              type: "success",
              content: `${found.status} ${literal}\n  Status: ${
                found.status === "+D" ? "Definitely proven" : "Defeasibly proven"
              }`,
            };
          }
          return {
            type: "info",
            content: `${found.status} ${literal}\n  Status: Not provable`,
          };

        case "why-not":
        case "whynot":
          if (!args[0]) {
            return { type: "error", content: "Usage: why-not <literal>" };
          }
          const whyLit = args[0];
          const whyFound = conclusions.find((c) => c.literal === whyLit);
          if (whyFound && (whyFound.status === "+D" || whyFound.status === "+d")) {
            return {
              type: "info",
              content: `"${whyLit}" IS provable (${whyFound.status})`,
            };
          }
          // Check for negation
          const negLit = whyLit.startsWith("-") ? whyLit.slice(1) : `-${whyLit}`;
          const negFound = conclusions.find((c) => c.literal === negLit);
          if (negFound && (negFound.status === "+D" || negFound.status === "+d")) {
            return {
              type: "info",
              content: `"${whyLit}" defeated by: ${negFound.status} ${negLit}`,
            };
          }
          return {
            type: "info",
            content: `"${whyLit}" not provable: no supporting rules fire or prerequisites missing`,
          };

        case "complete":
          if (!args[0]) {
            return { type: "error", content: "Usage: complete <task>" };
          }
          const taskToComplete = args[0];
          onAppend([`(given completed-${taskToComplete})`]);
          return {
            type: "success",
            content: `Marked ${taskToComplete} as completed`,
          };

        case "claim":
          if (!args[0]) {
            return { type: "error", content: "Usage: claim <task>" };
          }
          const taskToClaim = args[0];
          onAppend([`(given claimed-${taskToClaim})`]);
          return {
            type: "success",
            content: `Claimed ${taskToClaim}`,
          };

        case "clear":
          setHistory([]);
          return { type: "info", content: "" };

        case "":
          return { type: "info", content: "" };

        default:
          return {
            type: "error",
            content: `Unknown command: ${command}. Type 'help' for available commands.`,
          };
      }
    },
    [conclusions, tasks, assignments, readyTasks, completedTasks, blockedTasks, onAppend]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const result = executeCommand(input);
    if (result.content) {
      setHistory((prev) => [...prev, { cmd: input, result }]);
    }
    setInput("");
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const cmds = history.map((h) => h.cmd);
      if (cmds.length > 0) {
        const newIndex = historyIndex < cmds.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(cmds[cmds.length - 1 - newIndex] || "");
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        const cmds = history.map((h) => h.cmd);
        setInput(cmds[cmds.length - 1 - newIndex] || "");
      } else {
        setHistoryIndex(-1);
        setInput("");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-base-300">
          <span className="font-mono text-sm">hence&gt;</span>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-xs"
            aria-label="Close"
          >
            ESC
          </button>
        </div>

        {/* Output */}
        <div
          ref={outputRef}
          className="flex-1 overflow-auto p-4 font-mono text-sm space-y-2 min-h-[200px]"
        >
          {history.map((entry, i) => (
            <div key={i}>
              <div className="text-primary">
                <span className="text-base-content/50">$</span> {entry.cmd}
              </div>
              <div
                className={`ml-2 whitespace-pre-wrap ${
                  entry.result.type === "error"
                    ? "text-error"
                    : entry.result.type === "success"
                    ? "text-success"
                    : "text-base-content/80"
                }`}
              >
                {Array.isArray(entry.result.content)
                  ? entry.result.content.map((item, j) => (
                      <div key={j}>• {item}</div>
                    ))
                  : entry.result.content}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="border-t border-base-300 p-2">
          <div className="flex items-center gap-2">
            <span className="text-primary font-mono">$</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent outline-none font-mono"
              placeholder="Type 'help' for commands..."
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
